"use client";

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCart } from '@/context/CartContext';
import { useStore } from '@/context/StoreContext';
import { useAuth } from '@/context/AuthContext'; // <--- IMPORTAÇÃO DO CONTEXTO DE AUTENTICAÇÃO
import { formatCurrency } from '@/lib/formatters';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';
import styles from './Cart.module.css';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faStore, 
    faTrash, 
    faUser, 
    faMapMarkerAlt, 
    faCreditCard, 
    faMoneyBillWave, 
    faBarcode, 
    faQrcode, 
    faTruck,
    faChevronRight,
    faChevronLeft,
    faCheckCircle,
    faTimes
} from '@fortawesome/free-solid-svg-icons';

// --- INTERFACES ---
interface CartItem {
  id: number;
  name: string;
  price: string;
  imageUrl: string;
  quantity: number;
  type: 'product_variant' | 'service';
}

interface PaymentMethod {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
}

interface ShippingOption {
    id: number;
    name: string;
    price: string;
    delivery_time: number;
    company: {
        name: string;
        picture: string;
    };
}

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
}

const CustomInput = ({ label, id, ...props }: CustomInputProps) => (
    <div className={styles.inputGroup}>
        <label htmlFor={id}>{label}</label>
        <input id={id} {...props} className={styles.formInput} />
    </div>
);

const iconMap: { [key: string]: any } = {
  cash: faMoneyBillWave,
  credit_card: faCreditCard,
  debit_card: faCreditCard,
  pix: faQrcode, 
  bank_slip: faBarcode,
  default: faCreditCard,
};

export default function CartPage() {
    const { 
        closeCart, cartItems, updateQuantity, removeFromCart, clearCart,
        subTotal, appliedCoupon, discountAmount, 
        applyCoupon, removeCoupon,
        shippingCost, setShippingCost, finalTotal
    } = useCart();

    const { settings } = useStore();
    const { user, isAuthenticated, token } = useAuth(); // <--- CHAMA A AUTENTICAÇÃO AQUI

    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userInfo, setUserInfo] = useState({ fullName: '', phone: '', cpfCnpj: '' });
    const [address, setAddress] = useState({ cep: '', street: '', neighborhood: '', number: '', city: '', complement: '', state: '' });
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [selectedPaymentSlug, setSelectedPaymentSlug] = useState<string | null>(null);
    const [couponCode, setCouponCode] = useState('');
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    
    const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
    const [selectedShipping, setSelectedShipping] = useState<ShippingOption | null>(null);
    const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);

    const storeName = settings.store_name || "Loja Virtual";

    // Verifica se há métodos de pagamento online ativos no catálogo
    const hasOnlinePayments = paymentMethods.length > 0;

    const stepsList = useMemo(() => {
        if (hasOnlinePayments) {
            return [
                { id: 1, label: 'Carrinho' },
                { id: 2, label: 'Entrega' },
                { id: 3, label: 'Pagto' },
                { id: 4, label: 'Fim' }
            ];
        }
        return [
            { id: 1, label: 'Carrinho' },
            { id: 2, label: 'Entrega' },
            { id: 3, label: 'Fim' }
        ];
    }, [hasOnlinePayments]);

    // --- AUTO-PREENCHIMENTO DE DADOS DO USUÁRIO LOGADO ---
    useEffect(() => {
        if (user && isAuthenticated) {
            setUserInfo({
                fullName: user.name || '',
                phone: user.contacts?.[0]?.value || '',
                cpfCnpj: user.document_number || ''
            });

            if (user.addresses && user.addresses.length > 0) {
                const addr = user.addresses[0];
                setAddress({
                    cep: addr.zip_code || '',
                    street: addr.street || '',
                    neighborhood: addr.neighborhood || '',
                    number: addr.number || '',
                    city: addr.city || '',
                    complement: addr.complement || '',
                    state: addr.state || ''
                });
            }
        }
    }, [user, isAuthenticated]);

    // Lógica de Frete (Passo 2)
    const handleStep2ShippingLogic = useCallback(() => {
        if (selectedShipping || shippingCost > 0) return;

        setShippingCost(0);
        setSelectedShipping(null);
        setShippingOptions([]);

        if (settings.shipping_method === 'fixed' && settings.fixed_shipping_rate && settings.fixed_shipping_rate > 0) {
            const fixedOption: ShippingOption = {
                id: 99, name: 'Frete Fixo', price: settings.fixed_shipping_rate.toString(),
                delivery_time: 5, company: { name: storeName, picture: '' }
            };
            setSelectedShipping(fixedOption);
            setShippingCost(Number(settings.fixed_shipping_rate));
        } else if (settings.shipping_method === 'disabled') {
            const pickupOption: ShippingOption = {
                id: 100, name: 'Retirada na Loja', price: '0',
                delivery_time: 0, company: { name: storeName, picture: '' }
            };
            setSelectedShipping(pickupOption);
            setShippingCost(0);
        }
    }, [settings, setShippingCost, storeName, selectedShipping, shippingCost]);

    useEffect(() => {
        if (step === 2) {
            handleStep2ShippingLogic();
        }
    }, [step, handleStep2ShippingLogic]);

    // Busca Métodos de Pagamento
    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                const subdomain = getSubdomain();
                const apiBaseUrl = getApiBaseUrl();
                const response = await fetch(`${apiBaseUrl}/public/payment-methods`, {
                    headers: { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' },
                });
                if (!response.ok) throw new Error('Falha ao buscar formas de pagamento');
                const data = await response.json();
                setPaymentMethods(data);
                if (data.length > 0 && !selectedPaymentSlug) {
                    setSelectedPaymentSlug(data[0].slug);
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchPaymentMethods();
    }, [selectedPaymentSlug]);

    const handleUserInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => setAddress({ ...address, [e.target.name]: e.target.value });

    const formatPrice = (value: number) => formatCurrency(value, settings.currency);

    // Cálculo dinâmico da meta de Frete Grátis baseado na Aba 3 (Vantagens) ou método de envio
    const freeShippingThreshold = useMemo(() => {
        if (settings.benefits_bar?.items) {
            for (const item of settings.benefits_bar.items) {
                const text = `${item.title} ${item.subtitle}`.toLowerCase();
                if (text.includes('frete') && (text.includes('grátis') || text.includes('gratis'))) {
                    const match = text.match(/\d+([.,]\d+)?/);
                    if (match) {
                        const val = parseFloat(match[0].replace(',', '.'));
                        if (val > 0) return val;
                    }
                }
            }
        }
        if (settings.shipping_method && settings.shipping_method !== 'disabled') {
            return 199;
        }
        return 0;
    }, [settings.benefits_bar, settings.shipping_method]);

    const freeShippingProgress = freeShippingThreshold > 0 
        ? Math.min(100, Math.round((subTotal / freeShippingThreshold) * 100))
        : 100;
    const remainingForFreeShipping = freeShippingThreshold > 0 
        ? Math.max(0, freeShippingThreshold - subTotal)
        : 0;

    const handleApplyCoupon = async () => {
        setIsApplyingCoupon(true);
        await applyCoupon(couponCode);
        setIsApplyingCoupon(false);
    };

    const handleCalculateShipping = async () => {
        const cep = address.cep.replace(/\D/g, '');
        if (cep.length !== 8) {
            toast.error("Digite um CEP válido (8 números).");
            return;
        }

        setIsCalculatingShipping(true);
        setShippingOptions([]);
        setSelectedShipping(null);
        setShippingCost(0);

        try {
            const subdomain = getSubdomain();
            const apiBaseUrl = getApiBaseUrl();
            
            const payload = {
                cep,
                items: cartItems.filter(item => item.type === 'product_variant').map(item => ({ id: item.id, quantity: item.quantity }))
            };

            if(payload.items.length === 0) {
                toast.success("Frete grátis para serviços.");
                return;
            }

            const response = await fetch(`${apiBaseUrl}/public/shipping/calculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Tenant-Subdomain': subdomain },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error('Erro ao calcular frete.');

            const data = await response.json();
            setShippingOptions(data);
            if (data.length === 0) toast.error("Nenhuma opção de frete para este CEP.");

        } catch (error) {
            toast.error("Não foi possível calcular o frete.");
        } finally {
            setIsCalculatingShipping(false);
        }
    };

    const handleSelectShipping = (option: ShippingOption) => {
        setSelectedShipping(option);
        setShippingCost(parseFloat(option.price));
    };

    const handleSubmitOrder = async () => {
        setIsSubmitting(true);
        const loadingToast = toast.loading('Processando seu pedido...');

        try {
            const subdomain = getSubdomain();
            
            const payload = {
                userInfo,
                address,
                cartItems: cartItems.map(item => ({ id: item.id, quantity: item.quantity, type: item.type })),
                cartTotal: finalTotal,
                paymentMethod: hasOnlinePayments ? selectedPaymentSlug : null,
                couponCode: appliedCoupon?.code || null
            };

            const apiBaseUrl = getApiBaseUrl();
            const response = await fetch(`${apiBaseUrl}/public/orders`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Accept': 'application/json', 
                    'X-Tenant-Subdomain': subdomain,
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}) // <--- ENVIA O TOKEN SE ESTIVER LOGADO
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            toast.dismiss(loadingToast);

            if (!response.ok) throw new Error(result.message || 'Erro ao criar pedido');
            
            toast.success('Pedido realizado com sucesso! 🎉');
            clearCart();
            closeCart();
            if (result.sale_id) {
                const tokenParam = result.order_token ? `?t=${encodeURIComponent(result.order_token)}` : '';
                window.location.href = `/pedidos/${result.sale_id}${tokenParam}`;
            }

        } catch (error) {
            toast.dismiss(loadingToast);
            if (error instanceof Error) toast.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // --- RENDERIZADORES DE CADA PASSO ---

    const renderStep1 = () => (
        <div className="animate-fadeIn">
            <h2 className={styles.stepTitle}>Seu Carrinho</h2>

            {/* Barra de Progresso de Frete Grátis */}
            {cartItems.length > 0 && freeShippingThreshold > 0 && (
                <div className="mb-4 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex justify-between items-center text-xs font-bold mb-1.5">
                        {remainingForFreeShipping <= 0 ? (
                            <span className="text-emerald-600 flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faTruck} />
                                Parabéns! Você ganhou Frete Grátis 🎉
                            </span>
                        ) : (
                            <span className="text-gray-700 flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faTruck} className="text-[var(--primary)]" />
                                Faltam <strong className="text-[var(--primary)]">{formatPrice(remainingForFreeShipping)}</strong> para Frete Grátis!
                            </span>
                        )}
                        <span className="text-[10px] text-gray-400 font-medium">{freeShippingProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${freeShippingProgress}%` }}
                        />
                    </div>
                </div>
            )}

            {cartItems.length > 0 ? (
                <div className={styles.cartList}>
                    {cartItems.map((item: CartItem) => (
                        <div key={`${item.id}-${item.type}`} className={styles.cartItem}>
                            <div className={styles.cartItemImage}>
                                <img 
                                    src={item.imageUrl || 'https://placehold.co/100'} 
                                    alt={item.name} 
                                    onError={(e) => { e.currentTarget.src = 'https://placehold.co/100'; }}
                                />
                            </div>
                            <div className={styles.cartItemInfo}>
                                <h3>{item.name}</h3>
                                <p className={styles.itemPrice}>{formatPrice(parseFloat(item.price))}</p>
                                <div className={styles.quantityWrapper}>
                                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                                    <span>{item.quantity}</span>
                                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                                </div>
                            </div>
                            <div className={styles.cartItemTotal}>
                                <span>{formatPrice(parseFloat(item.price) * item.quantity)}</span>
                                <button onClick={() => removeFromCart(item.id)} className={styles.removeBtn}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    {/* Cupom */}
                    <div className={styles.couponContainer}>
                        <div className={styles.couponInputWrapper}>
                            <input 
                                type="text"
                                placeholder="Código do cupom"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                disabled={!!appliedCoupon}
                            />
                            <button onClick={handleApplyCoupon} disabled={isApplyingCoupon || !!appliedCoupon || !couponCode}>
                                {isApplyingCoupon ? '...' : 'Aplicar'}
                            </button>
                        </div>
                        {appliedCoupon && (
                            <div className={styles.couponActive}>
                                <span>Cupom <strong>{appliedCoupon.code}</strong> aplicado!</span>
                                <button onClick={removeCoupon}><FontAwesomeIcon icon={faTimes} /></button>
                            </div>
                        )}
                    </div>
                </div>
            ) : ( 
                <div className={styles.emptyState}>
                    <p>Seu carrinho está vazio.</p>
                    <button onClick={closeCart} className={styles.btnSecondary}>Continuar Comprando</button>
                </div>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div className="animate-fadeIn">
            <h2 className={styles.stepTitle}>Identificação e Entrega</h2>
            
            <div className={styles.formSection}>
                <h3>Seus Dados</h3>
                {/* LÓGICA DE USUÁRIO LOGADO */}
                {isAuthenticated ? (
                    <div className="bg-green-50 text-green-800 p-4 rounded-xl flex items-center justify-between gap-3 border border-green-100 mb-4">
                        <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={faUser} className="text-xl" />
                            <div>
                                <p className="font-bold text-base">{userInfo.fullName}</p>
                                <p className="text-sm opacity-80">Conectado. A compra será vinculada à sua conta.</p>
                                {userInfo.cpfCnpj && <p className="text-xs opacity-75 mt-0.5">CPF/CNPJ: {userInfo.cpfCnpj}</p>}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.inputGrid}>
                        <CustomInput label="Nome Completo" id="fullName" name="fullName" value={userInfo.fullName} onChange={handleUserInfoChange} required placeholder="Ex: João Silva" />
                        <CustomInput label="WhatsApp" id="phone" name="phone" value={userInfo.phone} onChange={handleUserInfoChange} required placeholder="(11) 99999-9999" />
                        <div className={styles.fullWidth}>
                            <CustomInput label="CPF ou CNPJ (Opcional para Pix / Obrigatório para Boleto)" id="cpfCnpj" name="cpfCnpj" value={userInfo.cpfCnpj} onChange={handleUserInfoChange} placeholder="000.000.000-00" />
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.formSection}>
                <h3>Endereço de Entrega</h3>
                <div className={styles.inputGrid}>
                    <div className={styles.fullWidth}><CustomInput label="CEP" id="cep" name="cep" value={address.cep} onChange={handleAddressChange} required placeholder="00000-000" /></div>
                    <div className={styles.fullWidth}><CustomInput label="Rua / Avenida" id="street" name="street" value={address.street} onChange={handleAddressChange} required /></div>
                    <CustomInput label="Número" id="number" name="number" value={address.number} onChange={handleAddressChange} required />
                    <CustomInput label="Bairro" id="neighborhood" name="neighborhood" value={address.neighborhood} onChange={handleAddressChange} required />
                    <CustomInput label="Cidade" id="city" name="city" value={address.city} onChange={handleAddressChange} required />
                    <CustomInput label="Estado" id="state" name="state" value={address.state} onChange={handleAddressChange} required placeholder="UF" maxLength={2} />
                    <div className={styles.fullWidth}><CustomInput label="Complemento (Opcional)" id="complement" name="complement" value={address.complement} onChange={handleAddressChange} /></div>
                </div>
            </div>

            <div className={styles.shippingSection}>
                <h3>Opções de Entrega</h3>
                {settings.shipping_method === 'melhor_envio' ? (
                    <>
                        <button className={styles.btnCalculate} onClick={handleCalculateShipping} disabled={isCalculatingShipping || !address.cep}>
                            {isCalculatingShipping ? 'Calculando...' : 'Ver Opções de Frete'}
                        </button>
                        
                        <div className={styles.shippingList}>
                            {shippingOptions.map(option => (
                                <div 
                                    key={option.id} 
                                    className={`${styles.shippingCard} ${selectedShipping?.id === option.id ? styles.selected : ''}`} 
                                    onClick={() => handleSelectShipping(option)}
                                >
                                    <div className={styles.shippingIcon}>
                                        {option.company.picture ? <img src={option.company.picture} alt="" /> : <FontAwesomeIcon icon={faTruck} />}
                                    </div>
                                    <div className={styles.shippingInfo}>
                                        <span className={styles.shippingName}>{option.name}</span>
                                        <span className={styles.shippingTime}>{option.delivery_time} dias úteis</span>
                                    </div>
                                    <div className={styles.shippingPrice}>{formatPrice(parseFloat(option.price))}</div>
                                    <div className={styles.radioIndicator}></div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className={styles.fixedShippingInfo}>
                        {settings.shipping_method === 'fixed' 
                            ? `Frete Fixo: ${formatPrice(Number(settings.fixed_shipping_rate))}`
                            : 'Retirada no local / A combinar'
                        }
                    </div>
                )}
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="animate-fadeIn">
            <h2 className={styles.stepTitle}>Pagamento</h2>
            <div className={styles.paymentGrid}>
                {paymentMethods.length > 0 ? paymentMethods.map(method => {
                    const Icon = iconMap[method.slug] || iconMap.default;
                    return (
                        <div 
                            key={method.id}
                            className={`${styles.paymentCard} ${selectedPaymentSlug === method.slug ? styles.selected : ''}`}
                            onClick={() => setSelectedPaymentSlug(method.slug)}
                        >
                            <div className={styles.paymentIcon}>
                                <FontAwesomeIcon icon={Icon} />
                            </div>
                            <span>{method.name}</span>
                            <div className={styles.radioIndicator}></div>
                        </div>
                    );
                }) : (
                    <p className="text-gray-500">Nenhum método de pagamento disponível.</p>
                )}
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="animate-fadeIn">
            <h2 className={styles.stepTitle}>Confirmar Pedido</h2>
            <div className={styles.reviewCard}>
                <div className={styles.reviewGroup}>
                    <h4><FontAwesomeIcon icon={faUser} /> Cliente</h4>
                    <p>{userInfo.fullName} <br/> {userInfo.phone} {userInfo.cpfCnpj ? ` • ${userInfo.cpfCnpj}` : ''}</p>
                </div>
                <div className={styles.reviewGroup}>
                    <h4><FontAwesomeIcon icon={faMapMarkerAlt} /> Entrega</h4>
                    <p>{address.street}, {address.number} - {address.neighborhood} <br/> {address.city}/{address.state}</p>
                    <p className="text-sm text-gray-500 mt-1">Método: {selectedShipping?.name || 'Padrão'}</p>
                </div>
                
                {hasOnlinePayments ? (
                    <div className={styles.reviewGroup}>
                        <h4><FontAwesomeIcon icon={faCreditCard} /> Pagamento</h4>
                        <p>{paymentMethods.find(m => m.slug === selectedPaymentSlug)?.name || 'Online'}</p>
                    </div>
                ) : (
                    <div className={styles.reviewGroup}>
                        <h4><FontAwesomeIcon icon={faStore} /> Modo do Pedido</h4>
                        <p className="text-amber-800 bg-amber-50 p-2.5 rounded-lg border border-amber-200 text-xs leading-relaxed">
                            <strong>Pedido para envio ao PDV:</strong> Os dados do seu pedido serão enviados para a loja, e nossa equipe entrará em contato para combinar a entrega e o pagamento.
                        </p>
                    </div>
                )}
                
                <div className={styles.reviewItems}>
                    <h4>Itens do Pedido</h4>
                    {cartItems.map(item => (
                        <div key={item.id} className={styles.reviewItemRow}>
                            <span>{item.quantity}x {item.name}</span>
                            <span>{formatPrice(parseFloat(item.price) * item.quantity)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.overlay}>
             <div className={styles.modalContainer}>
                 
                 <header className={styles.header}>
                     <div className={styles.headerBrand}>
                         <FontAwesomeIcon icon={faStore} className={styles.storeIcon} />
                         <span>{storeName}</span>
                     </div>
                     <button onClick={closeCart} className={styles.closeBtn}>
                        <FontAwesomeIcon icon={faTimes} />
                     </button>
                 </header>

                 <div className={styles.contentWrapper}>
                     
                     {cartItems.length > 0 && (
                         <div className={styles.stepper}>
                             {stepsList.map((s) => (
                                 <div key={s.id} className={`${styles.step} ${step >= s.id ? styles.stepActive : ''}`}>
                                     <div className={styles.stepCircle}>{s.id}</div>
                                     <div className={styles.stepLabel}>{s.label}</div>
                                 </div>
                             ))}
                             <div className={styles.stepLine}>
                                 <div 
                                     className={styles.stepLineProgress} 
                                     style={{ width: `${((step - 1) / (stepsList.length - 1)) * 100}%` }}
                                 ></div>
                             </div>
                         </div>
                     )}

                     <div className={styles.mainContent}>
                         <div className={styles.stepContent}>
                             {cartItems.length === 0 ? renderStep1() : (
                                 <>
                                     {step === 1 && renderStep1()}
                                     {step === 2 && renderStep2()}
                                     {hasOnlinePayments && step === 3 && renderStep3()}
                                     {hasOnlinePayments && step === 4 && renderStep4()}
                                     {!hasOnlinePayments && step === 3 && renderStep4()}
                                 </>
                             )}
                         </div>

                         {cartItems.length > 0 && (
                             <aside className={styles.summarySidebar}>
                                 <h3>Resumo</h3>
                                 
                                 <div className={styles.summaryRow}>
                                     <span>Subtotal</span>
                                     <span>{formatPrice(subTotal)}</span>
                                 </div>
                                 
                                 {appliedCoupon && (
                                     <div className={`${styles.summaryRow} ${styles.discount}`}>
                                         <span>Desconto</span>
                                         <span>- {formatPrice(discountAmount)}</span>
                                     </div>
                                 )}

                                 <div className={styles.summaryRow}>
                                     <span>Frete</span>
                                     <span>{shippingCost > 0 ? formatPrice(shippingCost) : (step < 2 ? 'Calculado a seguir' : 'Grátis')}</span>
                                 </div>

                                 <div className={styles.divider}></div>

                                 <div className={styles.totalRow}>
                                     <span>Total</span>
                                     <span>{formatPrice(finalTotal)}</span>
                                 </div>

                                 <div className={styles.actionButtons}>
                                     {step < stepsList.length ? (
                                         <button 
                                            className={styles.btnPrimary} 
                                            onClick={() => setStep(s => s + 1)}
                                            disabled={
                                                (step === 2 && (!userInfo.fullName || !address.street || !address.cep || (!selectedShipping && settings.shipping_method !== 'disabled'))) ||
                                                (hasOnlinePayments && step === 3 && !selectedPaymentSlug)
                                            }
                                         >
                                             {step === stepsList.length - 1 ? 'Revisar Pedido' : 'Continuar'} <FontAwesomeIcon icon={faChevronRight} />
                                         </button>
                                     ) : (
                                         <button 
                                            className={`${styles.btnPrimary} ${styles.btnSuccess}`} 
                                            onClick={handleSubmitOrder}
                                            disabled={isSubmitting}
                                         >
                                             {isSubmitting ? 'Enviando...' : (
                                                 hasOnlinePayments 
                                                     ? (selectedPaymentSlug?.includes('pix') ? 'Finalizar e Gerar Pix' : (selectedPaymentSlug?.includes('bank_slip') ? 'Finalizar e Gerar Boleto' : 'Confirmar Pedido'))
                                                     : 'Enviar Pedido para a Loja'
                                             )} <FontAwesomeIcon icon={faCheckCircle} />
                                         </button>
                                     )}

                                     {step > 1 && (
                                         <button className={styles.btnBack} onClick={() => setStep(s => s - 1)}>
                                             <FontAwesomeIcon icon={faChevronLeft} /> Voltar
                                         </button>
                                     )}
                                 </div>
                             </aside>
                         )}
                     </div>
                 </div>
             </div>
        </div>
    );
}